import {
  Cell as TanstackCell,
  ColumnDef,
  flexRender,
  Table as TanstackTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { COLUMN_DEFAULT_SIZE_XS } from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Stack } from 'src/core/ui';
import { SortStatus } from 'src/core/ui/SortButton/SortButton.utils';
import { SortButtonV3 } from 'src/core/ui/SortButton/SortButtonV3';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';

import {
  CellTooltipFunction,
  TablePaginationConfig,
  TableRowHeightVariant,
  TableV2Meta,
} from '../types';
import { getPinnedStyles } from '../utils/pinnedStyles';
import { ColumnHeaderFilter } from './ColumnHeaderFilter';
import * as styles from './Table.css';
import { TableRowCheckbox, TableSelectAllCheckbox } from './TableCheckbox';
import { TableProvider, useTableContext } from './TableContext';

// Root component
interface TableRootProps<T = any> {
  children: React.ReactNode;
  table: TanstackTable<T>;
  variant?: TableRowHeightVariant;
  pagination?: TablePaginationConfig;
  className?: string;
}

export const Root = forwardRef<HTMLDivElement, TableRootProps>(
  function TableRoot(
    { children, table, variant = 'normal', pagination, className },
    ref
  ) {
    const [hasOverflow, setHasOverflow] = useState(false);
    const [isScrolledToRight, setIsScrolledToRight] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const checkOverflow = () => {
        const overflow = container.scrollWidth > container.clientWidth;
        if (overflow !== hasOverflow) {
          setHasOverflow(overflow);
        }
      };

      const checkScroll = () => {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const isAtRight = scrollLeft + clientWidth >= scrollWidth - 1;
        if (isAtRight !== isScrolledToRight) {
          setIsScrolledToRight(isAtRight);
        }
      };

      const handleResize = () => {
        checkOverflow();
        checkScroll();
      };

      // Check on mount
      handleResize();

      // Check on scroll
      container.addEventListener('scroll', checkScroll);

      // Check on resize
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }, [hasOverflow, isScrolledToRight]);

    return (
      <TableProvider value={table}>
        <div
          ref={containerRef}
          className={clsx(
            styles.tableContainer,
            styles.tableBordered,
            (!hasOverflow || isScrolledToRight) && styles.noOverflow,
            className
          )}
        >
          <table className={clsx(styles.tableVariants[variant])}>
            {children}
          </table>
        </div>
      </TableProvider>
    );
  }
);

// Header component
interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const Header = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  function TableHeader({ children, className }, ref) {
    return (
      <thead ref={ref} className={clsx(styles.tableHeader, className)}>
        {children}
      </thead>
    );
  }
);

// Body component
interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const Body = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  function TableBody({ children, className }, ref) {
    return (
      <tbody ref={ref} className={className}>
        {children}
      </tbody>
    );
  }
);

// Row component
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  selectable?: boolean;
  selected?: boolean;
  className?: string;
}

export const Row = forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow(
    { children, selectable, selected, className, onClick, ...props },
    ref
  ) {
    return (
      <tr
        ref={ref}
        className={clsx(
          styles.tableRow,
          selectable && styles.tableRowSelectable,
          selected && styles.tableRowSelected,
          className
        )}
        onClick={onClick}
        tabIndex={selectable ? 0 : undefined}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

// Type guard to check if cellTooltip is a function
function isCellTooltipFunction<TData>(
  tooltip: any
): tooltip is CellTooltipFunction<TData> {
  return typeof tooltip === 'function';
}

// Cell component
interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  columnId?: string;
  className?: string;
  cellContext?: TanstackCell<any, any>;
}

export const Cell = forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell(
    { children, columnId, className, cellContext, ...props },
    ref
  ) {
    const table = useTableContext();
    const tanstackColumn =
      cellContext?.column ?? (columnId ? table.getColumn(columnId) : null);

    // Get width and pinning from the column (cellContext.column already has current state)
    const columnWidth = tanstackColumn?.getSize();
    const isPinned = tanstackColumn?.getIsPinned() || false;
    const pinnedStyles =
      tanstackColumn && isPinned ? getPinnedStyles(tanstackColumn) : {};

    const isEditable = tanstackColumn?.columnDef.meta?.isEditable ?? false;

    const cellTooltip = tanstackColumn?.columnDef.meta?.cellTooltip;
    let tooltipContent: React.ReactNode = null;

    if (cellTooltip && cellContext) {
      if (cellTooltip === true) {
        // Use cell value as tooltip
        tooltipContent = children;
      } else if (isCellTooltipFunction(cellTooltip)) {
        // Call function with value and row data
        const value = cellContext.getValue();
        const row = cellContext.row.original;
        tooltipContent = cellTooltip(value, row);
      } else {
        // Use static tooltip content
        tooltipContent = cellTooltip;
      }
    }

    return (
      <td
        ref={ref}
        className={clsx(
          styles.tableCell,
          isEditable && styles.editableCell,
          cellContext?.column.columnDef?.meta?.align &&
            styles.tableCellAlign[cellContext.column.columnDef.meta.align],
          isPinned && styles.pinnedColumn,
          isPinned === 'left' && styles.pinnedColumnLeft,
          isPinned === 'right' && styles.pinnedColumnRight,
          columnId === 'checkbox' && styles.noBorderCell,
          className
        )}
        style={{
          ...pinnedStyles,
          ...props.style,
          width: columnWidth,
          minWidth: tanstackColumn?.columnDef.minSize,
          maxWidth: tanstackColumn?.columnDef.maxSize,
          position: pinnedStyles.position || props.style?.position,
        }}
        {...props}
      >
        <TableCellDiv
          className={styles.tableCellInner}
          align={tanstackColumn?.columnDef?.meta?.align}
          showTooltip={Boolean(tooltipContent)}
          customTooltipContent={tooltipContent}
        >
          {children}
        </TableCellDiv>
      </td>
    );
  }
);

// Column Header component
interface ColumnHeaderProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  columnId?: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerContext?: any; // TanStack Header object - pass this to avoid querying table
}

export const ColumnHeader = forwardRef<HTMLTableCellElement, ColumnHeaderProps>(
  function TableColumnHeader(
    {
      children,
      columnId,
      sortable,
      align = 'left',
      className,
      onClick,
      headerContext,
      ...props
    },
    ref
  ) {
    const [isHovered, setIsHovered] = useState(false);

    const table = useTableContext();
    const header =
      headerContext ??
      (columnId
        ? table
            .getHeaderGroups()[0]
            ?.headers.find((h) => h.column.id === columnId)
        : null);
    const tanstackColumn =
      header?.column ?? (columnId ? table.getColumn(columnId) : null);

    const autoSizeColumn = useCallback(() => {
      if (!columnId || !tanstackColumn) return;

      let maxWidth = header.column?.minSize || COLUMN_DEFAULT_SIZE_XS;

      // Get the column index
      const columnIndex = table
        .getHeaderGroups()[0]
        ?.headers.findIndex((h) => h.column.id === columnId);

      if (columnIndex === -1) return;

      // Query the actual rendered cells in the DOM
      const columnCells = document.querySelectorAll(
        `td:nth-child(${columnIndex + 1})`
      );

      columnCells.forEach((cell, index) => {
        const cellElement = cell as HTMLElement;
        const innerContent = cell.querySelector(
          `.${styles.tableCellInner}`
        ) as HTMLElement;
        if (!innerContent) return;

        // Create a measuring container that mimics table cell constraints
        const measuringContainer = document.createElement('div');
        measuringContainer.style.cssText = `
          position: absolute;
          visibility: hidden;
          display: table;
        `;

        const measuringCell = document.createElement('div');
        measuringCell.style.cssText = `
          display: table-cell;
          white-space: nowrap;
        `;

        // Clone only the actual content, not the flex container
        const children = innerContent.children;
        if (children.length > 0) {
          // If there are child elements, clone them
          Array.from(children).forEach((child) => {
            measuringCell.appendChild(child.cloneNode(true));
          });
        } else {
          // If it's just text content, use that
          measuringCell.textContent = innerContent.textContent || '';
        }

        // Apply the same font styles
        const computedStyle = window.getComputedStyle(innerContent);
        measuringCell.style.font = computedStyle.font;
        measuringCell.style.fontSize = computedStyle.fontSize;
        measuringCell.style.fontFamily = computedStyle.fontFamily;
        measuringCell.style.fontWeight = computedStyle.fontWeight;
        measuringCell.style.letterSpacing = computedStyle.letterSpacing;
        measuringCell.style.textTransform = computedStyle.textTransform;

        measuringCell.style.lineHeight = computedStyle.lineHeight;

        measuringContainer.appendChild(measuringCell);
        document.body.appendChild(measuringContainer);

        const measuredWidth = measuringContainer.offsetWidth;
        const width = measuredWidth;

        const cellPadding =
          parseFloat(window.getComputedStyle(cellElement).paddingLeft || '0') +
          parseFloat(window.getComputedStyle(cellElement).paddingRight || '0');

        // Add padding from the cell itself plus a small buffer
        maxWidth = Math.max(maxWidth, width + cellPadding + 2);

        document.body.removeChild(measuringContainer);
      });

      // Respect max size if defined
      if (header.column?.maxSize) {
        maxWidth = Math.min(maxWidth, header.column.maxSize);
      }

      // Update column size
      table.setColumnSizing(() => ({
        [columnId]: maxWidth,
      }));
    }, [
      columnId,
      tanstackColumn,
      header.column?.minSize,
      header.column?.maxSize,
      table,
    ]);

    // Use TanStack methods when available
    const sortStatus = tanstackColumn?.getIsSorted()
      ? tanstackColumn.getIsSorted() === 'desc'
        ? SortStatus.DESC
        : SortStatus.ASC
      : null;

    const canSort =
      tanstackColumn?.getCanSort() ??
      header.column?.enableSorting ??
      sortable ??
      false;
    const canResize =
      tanstackColumn?.getCanResize() ?? header.column?.enableResizing ?? false;
    const canFilter = tanstackColumn?.getCanFilter() ?? false;
    const columnWidth = tanstackColumn?.getSize() || header.column?.size;
    const isPinned = tanstackColumn?.getIsPinned() || false;
    const pinnedStyles =
      tanstackColumn && isPinned ? getPinnedStyles(tanstackColumn) : {};

    const isColumnSorted = Boolean(tanstackColumn?.getIsSorted());
    const showSort = isHovered || isColumnSorted;

    const onSortClick = () => {
      if (canSort && tanstackColumn) {
        tanstackColumn.toggleSorting();
      }
    };

    return (
      <th
        ref={ref}
        className={clsx(
          styles.tableHeaderCell,
          styles.tableCellAlign[align],
          canSort && styles.sortableHeader,
          isPinned && styles.pinnedColumn,
          isPinned && styles.pinnedHeaderCell,
          isPinned === 'left' && styles.pinnedColumnLeft,
          isPinned === 'right' && styles.pinnedColumnRight,
          columnId === 'checkbox' && styles.noBorderCell,
          className
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...pinnedStyles,
          ...props.style,
          width: columnWidth,
          minWidth: header.column?.minSize,
          maxWidth: header.column?.maxSize,
          position: pinnedStyles.position || 'relative',
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          autoSizeColumn();
        }}
        {...props}
      >
        <Stack justifyContent="spaceBetween" alignItems="center">
          <TableCellDiv
            wrap="nowrap"
            align={align}
            showTooltip={Boolean(header.column?.meta?.headerTooltip)}
            showTooltipArrow={false}
            customTooltipContent={
              header.column?.meta?.headerTooltip === true
                ? children // Use header text as tooltip when headerTooltip is true
                : header.column?.meta?.headerTooltip
            }
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                minWidth: 0,
                width: '100%',
              }}
            >
              {children}
            </div>
          </TableCellDiv>
          <Stack direction="row" alignItems="center">
            {canFilter && columnId && header && (
              <ColumnHeaderFilter table={table} header={header} />
            )}
            {canSort && showSort && (
              <SortButtonV3
                sortStatus={sortStatus ?? SortStatus.UNSORTED}
                onClick={onSortClick}
              />
            )}
          </Stack>
        </Stack>
        {canResize && columnId && header && (
          <div
            className={clsx(
              styles.columnResizer,
              header.column.getIsResizing() && styles.columnResizerActive
            )}
            onMouseDown={(e) => {
              const handler = header.getResizeHandler();
              // Set height of column resizer when starting to resize
              const resizer = e.currentTarget as HTMLElement;
              const tableEl = resizer.closest('table');
              if (tableEl) {
                resizer.style.height = `${tableEl.offsetHeight}px`;
              }
              handler(e);
            }}
            onTouchStart={header.getResizeHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </th>
    );
  }
);

// SelectAll Checkbox component
export function SelectAllCheckbox() {
  const table = useTableContext();
  const isAllSelected = table.getIsAllRowsSelected();
  const isSomeSelected = table.getIsSomeRowsSelected();

  return (
    <input
      type="checkbox"
      className={styles.checkbox}
      checked={isAllSelected}
      ref={(el) => {
        if (el) el.indeterminate = isSomeSelected && !isAllSelected;
      }}
      onChange={table.getToggleAllRowsSelectedHandler()}
      aria-label="Select all rows"
    />
  );
}

// Row Checkbox component
interface RowCheckboxProps {
  rowId: string;
}

export function RowCheckbox({ rowId }: RowCheckboxProps) {
  const table = useTableContext();
  const row = table.getRow(rowId);
  const isSelected = row?.getIsSelected() ?? false;

  return (
    <input
      type="checkbox"
      className={styles.checkbox}
      checked={isSelected}
      onChange={row?.getToggleSelectedHandler()}
      onClick={(e) => e.stopPropagation()}
      aria-label="Select row"
    />
  );
}

// Footer component
interface TableFooterProps {
  children?: React.ReactNode;
  className?: string;
}

export const Footer = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  function TableFooter({ children, className }, ref) {
    const table = useTableContext();
    const footerGroups = table.getFooterGroups();
    const hasFooter = footerGroups.some((group) =>
      group.headers.some((header) => header.column.columnDef.footer)
    );
    const hasSummaryData =
      (table.options.meta as TableV2Meta)?.summaryData != null;

    if ((!hasFooter || !hasSummaryData) && !children) return null;

    const columns = (table.options.meta as TableV2Meta)?.columns || [];

    return (
      <tfoot ref={ref} className={clsx(styles.tableFooter, className)}>
        {children ||
          footerGroups.map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => {
                const column = columns.find(
                  (col: ColumnDef<any>) => col.id === header.column.id
                );
                const tanstackColumn = header.column;
                const columnWidth = tanstackColumn.getSize();
                const isPinned = tanstackColumn.getIsPinned() || false;
                const pinnedStyles =
                  tanstackColumn && isPinned
                    ? getPinnedStyles(tanstackColumn)
                    : {};

                return (
                  <td
                    key={header.id}
                    className={clsx(
                      styles.tableFooterCell,
                      column?.meta?.align &&
                        styles.tableCellAlign[column.meta.align],
                      isPinned && styles.pinnedColumn,
                      isPinned && styles.pinnedFooterCell,
                      isPinned === 'left' && styles.pinnedColumnLeft,
                      isPinned === 'right' && styles.pinnedColumnRight
                    )}
                    style={{
                      ...pinnedStyles,
                      width: columnWidth,
                      minWidth: column?.minSize,
                      maxWidth: column?.maxSize,
                      position: pinnedStyles.position || 'relative',
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.footer,
                      header.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
      </tfoot>
    );
  }
);

// Footer Cell component - renders a single <td> in footer
interface FooterCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  columnId?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerContext?: any; // TanStack Header object from footer group
}

export const FooterCell = forwardRef<HTMLTableCellElement, FooterCellProps>(
  function TableFooterCell(
    { children, className, columnId, align, headerContext, ...props },
    ref
  ) {
    const table = useTableContext();
    const footerGroups = table.getFooterGroups();
    const hasFooter = footerGroups.some((group) =>
      group.headers.some((header) => header.column.columnDef.footer)
    );
    const hasSummaryData =
      (table.options.meta as TableV2Meta)?.summaryData != null;

    if ((!hasFooter || !hasSummaryData) && !children) return null;

    const columns = (table.options.meta as TableV2Meta)?.columns || [];
    const header =
      headerContext ??
      (columnId
        ? table
            .getFooterGroups()[0]
            ?.headers.find((h) => h.column.id === columnId)
        : null);
    const tanstackColumn =
      header?.column ?? (columnId ? table.getColumn(columnId) : null);
    const column = columnId
      ? columns.find((col: ColumnDef<any>) => col.id === columnId)
      : null;

    const columnWidth = tanstackColumn?.getSize();
    const isPinned = tanstackColumn?.getIsPinned() || false;
    const pinnedStyles =
      tanstackColumn && isPinned ? getPinnedStyles(tanstackColumn) : {};

    return (
      <td
        className={clsx(
          styles.tableFooterCell,
          column?.meta?.align && styles.tableCellAlign[column.meta.align],
          isPinned && styles.pinnedColumn,
          isPinned && styles.pinnedFooterCell,
          isPinned === 'left' && styles.pinnedColumnLeft
        )}
        style={{
          ...pinnedStyles,
          width: columnWidth,
          minWidth: column?.minSize,
          maxWidth: column?.maxSize,
          position: pinnedStyles.position || 'relative',
        }}
      >
        {children}
      </td>
    );
  }
);

// Namespace export - all table components in one place
export const Table = {
  Root,
  Header,
  Body,
  Footer,
  FooterCell,
  Row,
  Cell,
  ColumnHeader,
  SelectAllCheckbox: TableSelectAllCheckbox,
  RowCheckbox: TableRowCheckbox,
};
